import { useAction, useAtom } from '@reatom/npm-react'
import styled, { css } from 'styled-components'

import { Button } from '@blade/ui-kit'
import {
  startNewWalletFlowEvent,
  walletsAtom,
} from '@root/entities/wallet/atoms'
import { WalletsList } from '@root/features/wallet/wallets-list'
import { useTranslation } from '@root/shared/localization'
import {
  Dialog,
  ExclamationIcon,
  InfoMessage,
  Separator,
} from '@root/shared/ui-kit'

import { WalletsDialogButton } from './wallets-dialog-button'

import type { CurrencyModel } from '@root/entities/currency'
import type { WalletModel } from '@root/entities/wallet/atoms'
import type {
  WalletsListItemsProps,
  WalletsListSelectionProps,
} from '@root/features/wallet/wallets-list/ui/wallets-list-items'
import type { DialogControlProps } from '@root/shared/ui-kit'

export type WalletsDialogProps = DialogControlProps &
  WalletsListSelectionProps & {
    wallets?: Array<WalletModel>
    currency?: CurrencyModel
    footerContent?: React.ReactNode
  }

export const WalletsDialog = ({
  open,
  onOpenChange,
  wallets: defaultWallets,
  footerContent,
  ...props
}: WalletsDialogProps) => {
  const { t } = useTranslation()

  const startNewWalletFlow = useAction(startNewWalletFlowEvent)
  const [allWallets] = useAtom(walletsAtom)
  const wallets = defaultWallets ?? allWallets

  // const walletsReordered = useMemo(() => {
  //   const activeWallet = wallets.find(
  //     (wallet) => wallet.id === selectedWalletId,
  //   )
  //   if (!activeWallet) {
  //     return wallets
  //   }

  //   return [
  //     activeWallet,
  //     ...wallets.filter((wallet) => wallet.id !== activeWallet.id),
  //   ]
  // }, [selectedWalletId, wallets])

  const isEmpty = wallets.length === 0

  const handleCreateWallet = () => {
    onOpenChange?.(false)
    startNewWalletFlow()
  }

  const renderWalletsOrEmpty = () => {
    if (isEmpty) {
      return (
        <InfoMessage
          intent="empty"
          variant="dt"
          withBackground
          icon={<ExclamationIcon />}
          title={t('wallet.no_wallets_title')}
          description={t('wallet.no_wallets_description')}
          retryText={t('wallet.create.new-wallet')}
          withSeparator
          css={css`
            padding-inline: 50px;
          `}
        >
          <ButtonCreateWallet
            size="xs"
            color="primary"
            radius="rounded"
            type="button"
            onClick={handleCreateWallet}
          >
            {t('wallet.create.new-wallet')}
          </ButtonCreateWallet>
        </InfoMessage>
      )
    }

    const listProps = {
      ...props,
      wallets,
    } as WalletsListItemsProps

    return <WalletsList {...listProps} />
  }

  const handleClose = () => onOpenChange?.(false)

  return (
    <Root
      variant="bordered"
      open={open}
      describedBy={t('wallet.select_wallet')}
      onOpenChange={(open) => {
        if (!open) {
          handleClose()
        }
      }}
    >
      <Dialog.Header title={t('wallet.select_wallet')} />
      <Content>
        <Separator />
        {renderWalletsOrEmpty()}
        {footerContent}
      </Content>
    </Root>
  )
}
WalletsDialog.Button = WalletsDialogButton

const Root = styled(Dialog)`
  background: var(--background-2);

  width: 418px;
  height: 656px;

  display: grid;
`

const ButtonCreateWallet = styled(Button)`
  height: 28px;
  font-weight: 500;
`

const Content = styled.div`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
`
